/**
 * Kyle Rising — track carousel arrows.
 *
 * The carousel itself is CSS scroll-snap; this only adds the desktop arrows and
 * keeps their state honest. Touch devices swipe and never see the buttons, so
 * everything here is a progressive enhancement over a list that already works.
 */
class KRCarousel extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-kr-carousel-track]');
    this.prev = this.querySelector('[data-kr-carousel-prev]');
    this.next = this.querySelector('[data-kr-carousel-next]');

    if (!this.track || !this.prev || !this.next) return;

    this.prev.addEventListener('click', () => this.#scrollBy(-1));
    this.next.addEventListener('click', () => this.#scrollBy(1));

    // Passive: this only reads scroll position, never blocks the gesture.
    this.track.addEventListener('scroll', () => this.#update(), { passive: true });
    window.addEventListener('resize', () => this.#update(), { passive: true });

    this.#update();
  }

  /** One card plus its gap, so a click advances exactly one cover. */
  #step() {
    const item = this.track.querySelector('li');
    if (!item) return this.track.clientWidth;

    const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
    return item.getBoundingClientRect().width + gap;
  }

  #scrollBy(direction) {
    this.track.scrollBy({ left: this.#step() * direction, behavior: 'smooth' });
  }

  #update() {
    const { scrollLeft, scrollWidth, clientWidth } = this.track;

    // 1px of slack: fractional widths mean these rarely land exactly.
    const atStart = scrollLeft <= 1;
    const atEnd = scrollLeft + clientWidth >= scrollWidth - 1;

    this.prev.disabled = atStart;
    this.next.disabled = atEnd;

    // Nothing to scroll: hide the controls rather than show two dead buttons.
    this.toggleAttribute('data-kr-carousel-idle', atStart && atEnd);
  }
}

if (!customElements.get('kr-carousel')) {
  customElements.define('kr-carousel', KRCarousel);
}
